import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionDenied} from '../../../../../srcCommon/permissions/permissionDenied';
import {inboxHasColor} from '../../../../helpers/modelHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../helpers/models/tagHelpers';
import {ConversationBaseModel} from '../../../../models/api/conversationModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {
  BulkActionsTrackingTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../services/tracking';
import {CellsContext} from '../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {ColorDot} from '../../../common/colorDot';
import {ConversationsArchiveButtonProps} from '../conversation/actions/archive/conversationsArchiveCoordinator';
import {ConversationsArchiveCoordinatorContainer} from '../conversation/actions/archive/conversationsArchiveCoordinatorContainer';
import {ConversationsMoreButtonProps} from '../conversation/actions/more/conversationsMoreCoordinator';
import {ConversationsMoreCoordinatorContainer} from '../conversation/actions/more/conversationsMoreCoordinatorContainer';
import {ConversationsMoveButtonProps} from '../conversation/actions/move/conversationsMoveCoordinator';
import {ConversationsMoveCoordinatorContainer} from '../conversation/actions/move/conversationsMoveCoordinatorContainer';
import {
  ConversationsTagButtonProps,
  ConversationsTagCoordinator,
} from '../conversation/actions/tag/conversationsTagCoordinator';
import {ConversationsTrashButtonProps} from '../conversation/actions/trash/conversationsTrashCoordinator';
import {ConversationsTrashCoordinatorContainer} from '../conversation/actions/trash/conversationsTrashCoordinatorContainer';

/*
 * Props.
 */

interface ConversationsViewerButtonsProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  areAllSpamOrTrashed: boolean;
}

/*
 * Style.
 */

const StyledViewerDiv = styled.div`
  margin-top: 28px;
  display: flex;
  flex-direction: row;
  max-width: 100%;
`;

const StyledButtonWrapperDiv = styled.div`
  margin: 0px 4px;
  flex-shrink: 0;
`;

const StyledShrinkableButtonWrapperDiv = styled(StyledButtonWrapperDiv)`
  flex-shrink: 1;
  overflow: hidden;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  padding: 0px 2px;
`;

const StyledLeftIcon = styled(Icon)`
  margin: 1px 9px 0px -1px;
`;

const StyledRightIcon = styled(Icon)`
  margin: 1px -4px 0px 5px;
`;

const StyledDotDiv = styled.div`
  margin: 5px 9px 4px -1px;
  height: 8px;
`;

/*
 * Component.
 */

export const ConversationsViewerButtons: FC<ConversationsViewerButtonsProps> = (props) => (
  <StyledViewerDiv>
    {!props.areAllSpamOrTrashed && (
      <StyledButtonWrapperDiv>
        <ConversationsArchiveCoordinatorContainer
          cellsContext={props.cellsContext}
          inboxesParameters={props.inboxesParameters}
          conversations={props.conversations}
          isBulkActionRequired={props.isBulkActionRequired}
          renderButton={(buttonProps) => renderArchiveButton(props, buttonProps)}
          panelId={props.panelId}
          onInterfaceEvent={onInterfaceEvent}
        />
      </StyledButtonWrapperDiv>
    )}
    <StyledButtonWrapperDiv>
      <ConversationsTrashCoordinatorContainer
        cellsContext={props.cellsContext}
        inboxesParameters={props.inboxesParameters}
        conversations={props.conversations}
        isBulkActionRequired={props.isBulkActionRequired}
        renderButton={(buttonProps) => renderTrashButton(props, buttonProps)}
        panelId={props.panelId}
        onInterfaceEvent={onInterfaceEvent}
      />
    </StyledButtonWrapperDiv>
    <StyledButtonWrapperDiv>
      <ConversationsTagCoordinator
        cellsContext={props.cellsContext}
        inboxesParameters={props.inboxesParameters}
        onlyConversation={undefined}
        conversations={props.conversations}
        isBulkActionRequired={props.isBulkActionRequired}
        renderButton={renderTagButton}
        panelId={props.panelId}
        onPopoverToggle={(isOpen) => onTagPopoverToggle(props, isOpen)}
        onInterfaceEvent={onInterfaceEvent}
        interfaceEventSource={ViewerDropdownInterfaceEventSourcesEnum.MULTI_SELECT_MODAL}
      />
    </StyledButtonWrapperDiv>
    {!props.areAllSpamOrTrashed && (
      <StyledShrinkableButtonWrapperDiv>
        <ConversationsMoveCoordinatorContainer
          cellsContext={props.cellsContext}
          inboxesParameters={props.inboxesParameters}
          conversations={props.conversations}
          isBulkActionRequired={props.isBulkActionRequired}
          renderButton={(buttonProps) => renderMoveButton(props, buttonProps)}
          panelId={props.panelId}
          onInterfaceEvent={onInterfaceEvent}
        />
      </StyledShrinkableButtonWrapperDiv>
    )}
    <StyledButtonWrapperDiv>
      <ConversationsMoreCoordinatorContainer
        cellsContext={props.cellsContext}
        inboxesParameters={props.inboxesParameters}
        onlyConversation={undefined}
        conversations={props.conversations}
        isBulkActionRequired={props.isBulkActionRequired}
        renderButton={(buttonProps) => renderMoreButton(props, buttonProps)}
        hasSnoozeItem
        hasSubscribeItem
        hasAssignItem
        hasStarItem
        panelId={props.panelId}
        onInterfaceEvent={onInterfaceEvent}
        isInMultiConversationsViewer
      />
    </StyledButtonWrapperDiv>
  </StyledViewerDiv>
);

/*
 * Tracking.
 */

function onInterfaceEvent(action: BulkActionsTrackingTypesEnum, isBulk: boolean) {
  trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_PERFORM_ACTION, {
    action,
    is_bulk: isBulk,
  });
}

/*
 * Archive.
 */

function renderArchiveButton(
  props: ConversationsViewerButtonsProps,
  buttonProps: ConversationsArchiveButtonProps,
) {
  const {permissionResult, onClick, areAllArchived} = buttonProps;
  const renderTooltip = permissionResult.isAllowed
    ? undefined
    : () => <PermissionDenied reason={permissionResult.reason} />;
  const onButtonClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_CLICK, {
      action: areAllArchived ? 'reopen' : 'archive',
      is_bulk: props.isBulkActionRequired,
    });

    onClick();
  };

  return (
    <UserThemeConsumer>
      {(theme) => (
        <Button
          isDisabled={!permissionResult.isAllowed}
          onClick={onButtonClick}
          renderTooltip={renderTooltip}
        >
          <StyledButtonContentDiv>
            <StyledLeftIcon
              name={areAllArchived ? 'archiveFilledSmall' : 'archiveSmall'}
              color={areAllArchived ? theme.palette.green.shade40 : undefined}
            />
            {areAllArchived ? (
              <Trans id="toolbar-reopen">Reopen</Trans>
            ) : (
              <Trans id="toolbar-archive">Archive</Trans>
            )}
          </StyledButtonContentDiv>
        </Button>
      )}
    </UserThemeConsumer>
  );
}

/*
 * Trash.
 */

function renderTrashButton(
  props: ConversationsViewerButtonsProps,
  buttonProps: ConversationsTrashButtonProps,
) {
  const {permissionResult, onClick, areAllTrashed, areAllSpam} = buttonProps;
  const renderTooltip = permissionResult.isAllowed
    ? undefined
    : () => <PermissionDenied reason={permissionResult.reason} />;
  const onButtonClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_CLICK, {
      action: areAllTrashed || areAllSpam ? 'restore' : 'trash',
      is_bulk: props.isBulkActionRequired,
    });

    onClick();
  };

  return (
    <Button isDisabled={!permissionResult.isAllowed} onClick={onButtonClick} renderTooltip={renderTooltip}>
      <StyledButtonContentDiv>
        {renderTrashButtonIcon(buttonProps)}
        {renderTrashButtonLabel(buttonProps)}
      </StyledButtonContentDiv>
    </Button>
  );
}

function renderTrashButtonIcon(buttonProps: ConversationsTrashButtonProps) {
  if (buttonProps.areAllTrashed) {
    return (
      <UserThemeConsumer>
        {(theme) => <StyledLeftIcon name="trashFilledSmall" color={theme.palette.red.shade40} />}
      </UserThemeConsumer>
    );
  }

  if (buttonProps.areAllSpam) {
    return (
      <UserThemeConsumer>
        {(theme) => <StyledLeftIcon name="spamFilledSmall" color={theme.palette.purple.shade40} />}
      </UserThemeConsumer>
    );
  }

  return <StyledLeftIcon name="trashSmall" />;
}

function renderTrashButtonLabel(buttonProps: ConversationsTrashButtonProps) {
  if (buttonProps.areAllTrashed || buttonProps.areAllSpam) {
    return <Trans id="toolbar-restore">Restore</Trans>;
  }

  return <Trans id="toolbar-trash">Trash</Trans>;
}

/*
 * Tag.
 */

function renderTagButton(buttonProps: ConversationsTagButtonProps) {
  const {permissionResult, hasHover} = buttonProps;
  const renderTooltip = permissionResult.isAllowed
    ? undefined
    : () => <PermissionDenied reason={permissionResult.reason} />;
  return (
    <Button isDisabled={!permissionResult.isAllowed} hasHover={hasHover} renderTooltip={renderTooltip}>
      <StyledButtonContentDiv>
        <StyledLeftIcon name="tagSmall" />
        <Trans id="conversations-viewer-tag">Tag</Trans>
      </StyledButtonContentDiv>
    </Button>
  );
}

function onTagPopoverToggle(props: ConversationsViewerButtonsProps, isOpen: boolean) {
  if (!isOpen) {
    return;
  }

  trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_CLICK, {
    action: 'tag',
    is_bulk: props.isBulkActionRequired,
  });
}

/*
 * Move.
 */

function renderMoveButton(props: ConversationsViewerButtonsProps, buttonProps: ConversationsMoveButtonProps) {
  const {permissionResult, hasHover, buttonRef, onClick, conversationInboxes} = buttonProps;
  const renderTooltip = permissionResult.isAllowed
    ? undefined
    : () => <PermissionDenied reason={permissionResult.reason} />;
  const onButtonClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_CLICK, {
      action: 'move',
      is_bulk: props.isBulkActionRequired,
    });

    onClick();
  };

  return (
    <Button
      ref={buttonRef}
      onClick={onButtonClick}
      hasHover={hasHover}
      isDisabled={!permissionResult.isAllowed}
      renderTooltip={renderTooltip}
    >
      {renderMoveButtonContent(conversationInboxes)}
    </Button>
  );
}

function renderMoveButtonContent(conversationInboxes: ReadonlyArray<InboxModel>) {
  // Multi-inbox case.
  if (conversationInboxes.length !== 1) {
    return (
      <StyledButtonContentDiv>
        <StyledLeftIcon name="moveRightSmall" />
        <Trans id="conversations-viewer-move">Move</Trans>
      </StyledButtonContentDiv>
    );
  }

  // Single inbox case.
  const inbox = conversationInboxes[0];
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledButtonContentDiv>
          {maybeRenderColorDot(inbox)}
          <TooltipCoordinator render={() => inbox.name}>
            <TooltipOverflowCondition>{inbox.name}</TooltipOverflowCondition>
          </TooltipCoordinator>
          <StyledRightIcon name="chevronDownSmall" color={theme.greys.shade80} />
        </StyledButtonContentDiv>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderColorDot(inbox: InboxModel) {
  if (!inboxHasColor(inbox)) {
    return null;
  }

  return (
    <StyledDotDiv>
      <ColorDot color={inbox.color} size={8} />
    </StyledDotDiv>
  );
}

/*
 * More.
 */

function renderMoreButton(props: ConversationsViewerButtonsProps, buttonProps: ConversationsMoreButtonProps) {
  const {hasHover, buttonRef, onClick} = buttonProps;
  const onButtonClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_CLICK, {
      action: 'more',
      is_bulk: props.isBulkActionRequired,
    });

    onClick();
  };

  return (
    <UserThemeConsumer>
      {(theme) => (
        <Button ref={buttonRef} onClick={onButtonClick} hasHover={hasHover}>
          <StyledButtonContentDiv>
            <Trans id="conversations-viewer-more">More</Trans>
            <StyledRightIcon name="chevronDownSmall" color={theme.greys.shade80} />
          </StyledButtonContentDiv>
        </Button>
      )}
    </UserThemeConsumer>
  );
}
